import React, { useState, useEffect, useCallback } from "react";
import {
  MessageSquare,
  Users,
  Zap,
  Clock,
  TrendingUp,
  RefreshCw,
  BarChart,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const PERIODS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

const MetricCard = ({ icon: Icon, label, value, subtext, color = "blue" }) => {
  const bgMap = {
    blue: "bg-blue-50",
    green: "bg-green-50",
    purple: "bg-purple-50",
    amber: "bg-amber-50",
    pink: "bg-pink-50",
  };
  const textMap = {
    blue: "text-blue-600",
    green: "text-green-600",
    purple: "text-purple-600",
    amber: "text-amber-600",
    pink: "text-pink-600",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${bgMap[color]}`}>
          <Icon className={`w-5 h-5 ${textMap[color]}`} />
        </div>
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    </div>
  );
};

const ChannelBar = ({ channel, count, maxCount }) => {
  const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const colors = {
    widget: "bg-blue-500",
    api: "bg-purple-500",
    slack: "bg-yellow-500",
    sms: "bg-green-500",
    email: "bg-pink-500",
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-slate-600 w-16 capitalize">{channel}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full ${colors[channel] || "bg-slate-400"}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <span className="text-sm text-slate-700 w-12 text-right">{count}</span>
    </div>
  );
};

export default function ClientDashboard() {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/my/facilities`, { headers });
      const arr = await res.json();
      if (Array.isArray(arr)) {
        setFacilities(arr);
        if (arr.length > 0 && !selectedFacility) {
          setSelectedFacility(arr[0].facility_id);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsage = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/my/usage?facility_id=${encodeURIComponent(selectedFacility)}&period=${period}`,
        { headers },
      );
      const json = await res.json();
      if (!json.error) setData(json);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [selectedFacility, period, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const calcCost = (tokIn, tokOut) => {
    const inCost = ((tokIn || 0) * 0.00015) / 1000;
    const outCost = ((tokOut || 0) * 0.0006) / 1000;
    return (inCost + outCost).toFixed(2);
  };

  const summary = data?.summary || {};
  const monthly = data?.monthly || [];
  const channels = data?.channels || [];
  const maxChannel = Math.max(...channels.map((c) => c.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Usage Dashboard
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Conversation and token usage for your facilities
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name || f.display_name || f.facility_id}
                </option>
              ))}
            </select>

            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    period === p.value
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <button
              onClick={fetchUsage}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard
              icon={MessageSquare}
              label="Conversations"
              value={(summary.total_conversations || 0).toLocaleString()}
              color="blue"
            />
            <MetricCard
              icon={Users}
              label="Unique Sessions"
              value={(summary.unique_sessions || 0).toLocaleString()}
              color="green"
            />
            <MetricCard
              icon={Zap}
              label="Tokens In"
              value={(summary.total_tokens_in || 0).toLocaleString()}
              color="purple"
            />
            <MetricCard
              icon={Zap}
              label="Tokens Out"
              value={(summary.total_tokens_out || 0).toLocaleString()}
              color="amber"
            />
            <MetricCard
              icon={TrendingUp}
              label="Avg Relevance"
              value={`${((summary.avg_relevance || 0) * 100).toFixed(0)}%`}
              color="blue"
            />
            <MetricCard
              icon={Clock}
              label="Est. LLM Cost"
              value={`$${calcCost(summary.total_tokens_in, summary.total_tokens_out)}`}
              color="pink"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                <BarChart className="w-4 h-4 mr-2 text-slate-400" />
                Monthly Breakdown
              </h4>
              {monthly.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No data available
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 text-slate-500 font-medium">
                          Month
                        </th>
                        <th className="text-right py-2 text-slate-500 font-medium">
                          Conversations
                        </th>
                        <th className="text-right py-2 text-slate-500 font-medium">
                          Sessions
                        </th>
                        <th className="text-right py-2 text-slate-500 font-medium">
                          Tokens
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly.map((m) => (
                        <tr
                          key={m.month}
                          className="border-b border-slate-50 hover:bg-slate-50"
                        >
                          <td className="py-2 text-slate-700">{m.month}</td>
                          <td className="py-2 text-right text-slate-700">
                            {(m.conversations || 0).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-slate-700">
                            {(m.sessions || 0).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-slate-700">
                            {(
                              (m.tokens_in || 0) + (m.tokens_out || 0)
                            ).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Channel Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center">
                <BarChart className="w-4 h-4 mr-2 text-slate-400" />
                Channel Breakdown
              </h4>
              {channels.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No data available
                </p>
              ) : (
                <div className="space-y-3">
                  {channels.map((c) => (
                    <ChannelBar
                      key={c.channel}
                      channel={c.channel}
                      count={c.count}
                      maxCount={maxChannel}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
